import { Box, Tooltip, TooltipProps } from "@mui/material";

type MUITooltipPlacement = TooltipProps["placement"];

interface Props {
	children: React.ReactElement<any, any>;
	title: React.ReactNode;
	placement?: MUITooltipPlacement;
	disableInteractive?: boolean;
	/**
	 * Let the disabled-child wrapper grow with its container instead of shrinking to fit.
	 *
	 * Only meaningful for a child that is sized by its parent — a `fullWidth` Button, or one
	 * carrying `sx={{ flex: 1 }}` — because the wrapper below becomes the flex item in its
	 * place. Everywhere else the default shrink-to-fit wrapper is already layout-neutral.
	 */
	stretch?: boolean;
}

// The tooltip's look (fill, type, padding, radius, shadow, arrow) is the theme's `MuiTooltip`
// override in providers/theme.ts, so every Tooltip in the app shares it. All this component
// adds is the disabled-child wrapper below.

// `inline-flex` keeps the child's own box intact and shrink-to-fit, which is layout-neutral for
// the icon/text buttons in a toolbar. `stretch` opts in for the few children sized by their
// container, where the wrapper, not the child, is now the flex item.
const wrapSx = { display: "inline-flex" } as const;
const wrapStretchSx = { display: "inline-flex", flex: "1 1 auto", width: "100%" } as const;

export function PortalTooltip({ children, title, placement, disableInteractive, stretch }: Props) {
	// A disabled <button> fires no pointer events at all, so MUI's Tooltip never sees the hover:
	// it warns ("Add a simple wrapper element, such as a `span`") and — the part that actually
	// matters — silently shows nothing, precisely when the user most wants to know *why* the
	// control is unavailable. The span is a live hover target that a disabled child cannot be.
	//
	// Wrapping is conditional so the ~160 enabled call sites keep the exact DOM they have today;
	// only a child that is disabled right now gains a node. The class names are stable hooks for
	// tests; the styling is the sx.
	const isDisabled = Boolean(children.props?.disabled);
	const wrapperClass = stretch ? "portal-tooltip-wrap portal-tooltip-wrap-stretch" : "portal-tooltip-wrap";

	return (
		<Tooltip placement={placement} title={title} disableInteractive={disableInteractive}>
			{isDisabled ? (
				<Box component="span" className={wrapperClass} sx={stretch ? wrapStretchSx : wrapSx}>
					{children}
				</Box>
			) : (
				children
			)}
		</Tooltip>
	);
}
